/**
 * @file zendesk/library.js
 * @description Make API requests to zendesk.
 * @copyright 2019 CBC/Radio-Canada. All rights reserved.
 */

import { request } from "../promises/library";
import log from "../logger/library";
import { HttpError } from "../errors/http";
import { ZENDESK_API_REQUESTS_URL } from "./constants";

export const createOptions = (data) => {
    return {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    };
};

/**
 * A function to get Zendesk form fields from a form Id.
 *
 * @param {object} formId - ID of Zendesk form
 * @returns {Promise}
 */

export function createTicket(data) {
    return request(ZENDESK_API_REQUESTS_URL, createOptions(data))
        .then((res) => {
            if (!res.ok) {
                throw new HttpError(
                    res.status,
                    `${res.status}: ${res.statusText}`
                );
            }
            return res;
        })
        .catch((error) => {
            log.error("(createTicket) Zendesk error", error);
            throw error;
        });
}

/**
 * Generate API payload for creating a typo form
 * @return {object} zendesk form payload
 */
export const processTypoForm = (fields, config, context) => {
    const dropdownValue = fields.typo_dropdown;
    const dropdownName = config.fields.typo_dropdown.options.find(
        (option) => option.value === dropdownValue
    ).name;
    const ocelotURL = `https://www.cbc.ca/${context.path}`;
    const polopolyURL = `http://polopoly.nm.cbc.ca:8080/polopoly/opencontent?contentInEdit=${context.polopolyId}`;
    const trunkcatedTitle = context.title
        .split("|")
        .slice(0, -1)
        .join("|")
        .trim();

    const subject = `story "${trunkcatedTitle}" has an ${dropdownName}`;

    let body = `<p>Hi!</p><p>A new typo report has been submitted from ${context.userName}&lt;${context.email}&gt;.</p>`;
    body += `<q>${fields.description}</q>`;
    body += `<p>Found in the story "<a href='${ocelotURL}'>${trunkcatedTitle}</a>".</p>`;
    body += `<p>It can be <a href='${polopolyURL}'>found on polopoly under content id ${context.polopolyId}</a></p>`;

    //compile input data into zendesk field objects
    const customFields = Object.keys(fields)
        .filter((name) => name !== "description")
        .map((name) => {
            return {
                id: config.fields[name].zendesk_id.toString(),
                value: fields[name],
            };
        });

    //add Ocelot URL and polopoly id to the list of zendesk fields
    customFields.push({
        id: config.staticFields.url.zendesk_id,
        value: `www.cbc.ca/${context.path}`,
    });
    customFields.push({
        id: config.staticFields.polopolyId.zendesk_id,
        value: context.polopolyId,
    });

    //add tags
    let tags = [];
    if (config.formTags) tags = tags.concat(config.formTags);
    if (context.department) tags.push("department_" + context.department);

    //return the API payload
    return {
        request: {
            requester: {
                name: context.userName,
                email: context.email,
            },
            subject,
            comment: {
                html_body: body,
            },
            tags,
            custom_fields: customFields,
            ticket_form_id: config.zendesk_id,
        },
    };
};

/**
 * A function to validate email addresses
 *
 * @param {String} text - the text to be validated as email
 * @returns {Boolean}

 */

export const emailValidationRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function emailValidation(text = "") {
    if (!(typeof text === "string")) {
        return false;
    }
    return Boolean(text.match(emailValidationRegex));
}
